import * as React from "react";
import { AnalysisSet, FvcAnalysisSet, PefAnalysisSet } from "webportalclient";
import { AcceptabilityActionTypes, AcceptabilityActions } from "./UseAcceptability";
import { ArtifactAcceptabilityIds, FvcParameters } from "./PFTAPITypes";

export interface AcceptabilityNextGenState {
    analysisSet: FvcAnalysisSet;
    loadingBestValues: boolean;
    loadingReversibilityChanges: boolean;
    changedAcceptability: boolean;
}

const initialState: AcceptabilityNextGenState = {
    analysisSet: undefined,
    loadingBestValues: false,
    loadingReversibilityChanges: false,
    changedAcceptability: false,
}

export enum AcceptabilityActionTypesNextGen {
    LoadInitialAcceptabilityNextGen = "LoadInitialAcceptabilityNextGen",
    UpdateAnalysisSet = "UpdateAnalysisSet",
    UpdateBestTestNextGen = "UpdateBestTestNextGen",
    UpdateGrading = "UpdateGrading",
    UpdatePefBestTestNextGen = "UpdatePefBestTestNextGen",
    UpdateReversibilityChangeNextGen = "UpdateReversibilityChangeNextGen"
}

type AcceptabilityActionTypesBoth = AcceptabilityActionTypes | AcceptabilityActionTypesNextGen;

type AcceptabilityNextGenActions = LoadInitialAcceptabilityNextGenAction | UpdateAnalysisSetAction | UpdateBestTestNextGenAction | UpdateGradingAction | AcceptabilityActions | UpdatePefBestTestNextGenAction | UpdateReversibilityChangeNextGenAction;

export class LoadInitialAcceptabilityNextGenAction {
    type: AcceptabilityActionTypesNextGen.LoadInitialAcceptabilityNextGen;
    analysisSet: FvcAnalysisSet;
    comparisonAnalysisSet: FvcAnalysisSet;

    static create(analysisSet: AnalysisSet, comparisonAnalysisSet: AnalysisSet): LoadInitialAcceptabilityNextGenAction {
        return {
            type: AcceptabilityActionTypesNextGen.LoadInitialAcceptabilityNextGen,
            analysisSet: analysisSet,
            comparisonAnalysisSet: comparisonAnalysisSet,
        };
    }
}

export class UpdateAnalysisSetAction {
    type: AcceptabilityActionTypesNextGen.UpdateAnalysisSet;
    updatedAnalysisSet: FvcAnalysisSet;

    static create(updatedAnalysisSet: AnalysisSet): UpdateAnalysisSetAction {
        return {
            type: AcceptabilityActionTypesNextGen.UpdateAnalysisSet,
            updatedAnalysisSet: updatedAnalysisSet,
        };
    }
}

export class UpdateBestTestNextGenAction {
    type: AcceptabilityActionTypesNextGen.UpdateBestTestNextGen;
    fvcParameters: FvcParameters;

    static create(fvcParameters: FvcParameters): UpdateBestTestNextGenAction {
        return {
            type: AcceptabilityActionTypesNextGen.UpdateBestTestNextGen,
            fvcParameters: fvcParameters,
        };
    }
}

export class ReversibilityChangeParemter {
    absolute: number;
    percentage: number;
}

export class UpdateReversibilityChangeNextGenAction {
    type: AcceptabilityActionTypesNextGen.UpdateReversibilityChangeNextGen;
    fev1ReversibilityChange: ReversibilityChangeParemter;

    static create(fev1ReversibilityChange: ReversibilityChangeParemter): UpdateReversibilityChangeNextGenAction {
        return {
            type: AcceptabilityActionTypesNextGen.UpdateReversibilityChangeNextGen,
            fev1ReversibilityChange: fev1ReversibilityChange
        };
    }
}

export class UpdatePefBestTestNextGenAction {
    type: AcceptabilityActionTypesNextGen.UpdatePefBestTestNextGen;
    bestPefMin: number;
    bestPefSec: number;

    static create(bestPefMin: number, bestPefSec: number): UpdatePefBestTestNextGenAction {
        return {
            type: AcceptabilityActionTypesNextGen.UpdatePefBestTestNextGen,
            bestPefMin: bestPefMin,
            bestPefSec: bestPefSec,
        };
    }
}

export class UpdateGradingAction {
    type: AcceptabilityActionTypesNextGen.UpdateGrading;
    fev1Grade: string;
    fvcGrade: string;

    static create(fev1Grade: string, fvcGrade: string): UpdateGradingAction {
        return {
            type: AcceptabilityActionTypesNextGen.UpdateGrading,
            fev1Grade: fev1Grade,
            fvcGrade: fvcGrade
        };
    }
}

export const useAcceptabilityNextGen = (analysisSet: FvcAnalysisSet & PefAnalysisSet, comparisonAnalysisSet?: FvcAnalysisSet & PefAnalysisSet): [AcceptabilityNextGenState, React.Dispatch<any>] => {
    const reducer = (state: AcceptabilityNextGenState, action: AcceptabilityNextGenActions): AcceptabilityNextGenState => {
        switch (action.type) {
            case AcceptabilityActionTypesNextGen.LoadInitialAcceptabilityNextGen:
                if (!action.analysisSet || !comparisonAnalysisSet) {
                    return {
                        ...state,
                        analysisSet: action.analysisSet
                    };
                }

                const mappedState = matchOverreaderAcceptabilityToSite(state, action, comparisonAnalysisSet);

                if (mappedState)
                    return mappedState;

                return {
                    ...state,
                    analysisSet: action.analysisSet,
                };
            case AcceptabilityActionTypesNextGen.UpdateAnalysisSet:
                const newTests = action.updatedAnalysisSet.tests.filter(u => !state.analysisSet.tests.map(t => t.id).includes(u.id));
                return {
                    ...state,
                    analysisSet: {
                        ...state.analysisSet,
                        tests: [...state.analysisSet.tests, ...newTests]
                    }
                }
            case AcceptabilityActionTypes.ToggleTestAcceptability:
                return {
                    ...state,
                    analysisSet: {
                        ...state.analysisSet,
                        tests: state.analysisSet.tests.map(t => {
                            if (t.testId === action.testId) {
                                return {
                                    ...t,
                                    accepted: !t.accepted,
                                    fev1Acceptability: action.checked === true ? ArtifactAcceptabilityIds.Acceptable : t.fev1Acceptability,
                                    fvcAcceptability: action.checked === true ? ArtifactAcceptabilityIds.Acceptable : t.fvcAcceptability,
                                }
                            } else {
                                return t;
                            }
                        }),
                    },
                    changedAcceptability: true
                };
            case AcceptabilityActionTypes.ToggleTestFev1Acceptability:
                return {
                    ...state,
                    analysisSet: {
                        ...state.analysisSet,
                        tests: state.analysisSet.tests.map(t => {
                            if (t.testId === action.testId) {
                                return {
                                    ...t,
                                    fev1Acceptability: action.value,
                                }
                            } else {
                                return t;
                            }
                        }),
                    },
                    changedAcceptability: true
                };
            case AcceptabilityActionTypes.ToggleTestFvcAcceptability:
                return {
                    ...state,
                    analysisSet: {
                        ...state.analysisSet,
                        tests: state.analysisSet.tests.map(t => {
                            if (t.testId === action.testId) {
                                return {
                                    ...t,
                                    fvcAcceptability: action.value,
                                }
                            } else {
                                return t;
                            }
                        }),
                    },
                    changedAcceptability: true
                };
            case AcceptabilityActionTypes.LoadingBestValues:
                return {
                    ...state,
                    loadingBestValues: true,
                    changedAcceptability: false
                };
            case AcceptabilityActionTypes.DoneLoadingBestValues:
                return {
                    ...state,
                    loadingBestValues: false,
                    changedAcceptability: false
                };

            case AcceptabilityActionTypes.LoadingReversibilityChanges:
                return {
                    ...state,
                    loadingReversibilityChanges: true,
                    changedAcceptability: false
                };
            case AcceptabilityActionTypes.DoneLoadingReversibilityChanges:
                return {
                    ...state,
                    loadingReversibilityChanges: false,
                    changedAcceptability: false
                };
            case AcceptabilityActionTypesNextGen.UpdateBestTestNextGen:
                return {
                    ...state,
                    analysisSet: {
                        ...state.analysisSet,
                        bestFev1: action.fvcParameters.Fev1,
                        bestFvc: action.fvcParameters.Fvc,
                        bestFev6: action.fvcParameters.Fev6,
                        bestTv: action.fvcParameters.Tv,
                        bestFivc: action.fvcParameters.Fivc,
                        bestPefMin: action.fvcParameters.PefMin,
                        bestPefSec: action.fvcParameters.PefSec,
                        bestPifMin: action.fvcParameters.PifMin,
                        bestPifSec: action.fvcParameters.PifSec,
                        bestFev3: action.fvcParameters.Fev3,
                        bestFev05: action.fvcParameters.Fev05,
                        bestFev075: action.fvcParameters.Fev075,
                        bestFiv1: action.fvcParameters.Fiv1,
                        bestMvvind: action.fvcParameters.Mvvind,
                        bestIvc: action.fvcParameters.Ivc,
                        bestFefMax: action.fvcParameters.FefMax,
                        bestFev1Vc: action.fvcParameters.Fev1Vc,
                        bestFev1Ht2: action.fvcParameters.Fev1Ht2,
                        bestFev3Vc: action.fvcParameters.Fev3Vc,
                        bestTlc: action.fvcParameters.Tlc,
                        bestRv: action.fvcParameters.Rv,
                        bestFrc: action.fvcParameters.Frc,
                        bestFev1Q: action.fvcParameters.Fev1Q,
                        bestFef2575: action.fvcParameters.Fef2575,
                        bestFef0212: action.fvcParameters.Fef0212,
                        bestFef7585: action.fvcParameters.Fef7585,
                        bestFef25: action.fvcParameters.Fef25,
                        bestFef50: action.fvcParameters.Fef50,
                        bestFef75: action.fvcParameters.Fef75,
                        bestFmft: action.fvcParameters.Fmft,
                        bestFev1Fev6: action.fvcParameters.Fev1Fev6,
                        bestFev1Fvc: action.fvcParameters.Fev1Fvc,
                        bestFev1R: action.fvcParameters.Fev1R,
                        bestFev05Fvc: action.fvcParameters.Fev05Fvc,
                        bestFev075Fvc: action.fvcParameters.Fev075Fvc,
                        bestFev3Fvc: action.fvcParameters.Fev3Fvc,
                        bestFev6Fvc: action.fvcParameters.Fev6Fvc,
                        bestFef2575Fvc: action.fvcParameters.Fef2575Fvc,
                        bestFivcFvc: action.fvcParameters.FivcFvc,
                        bestFev1Pef: action.fvcParameters.Fev1Pef,
                        bestFif50Fef50: action.fvcParameters.Fif50Fef50,
                        bestFev1Fivc: action.fvcParameters.Fev1Fivc,
                        bestFiv1Fivc: action.fvcParameters.Fiv1Fivc,
                        bestFiv1Fvc: action.fvcParameters.Fiv1Fvc,
                        bestFef50Fif50: action.fvcParameters.Fef50Fif50,
                        bestFev1Ivc: action.fvcParameters.Fev1Ivc,
                        bestFev3Fev6: action.fvcParameters.Fev3Fev6,
                        bestIc: action.fvcParameters.Ic,
                        bestFet: action.fvcParameters.Fet,
                        bestFif25: action.fvcParameters.Fif25,
                        bestFif50: action.fvcParameters.Fif50,
                        bestFif75: action.fvcParameters.Fif75,
                        bestRawind: action.fvcParameters.Rawind,
                        bestIrv: action.fvcParameters.Irv,
                        bestErv: action.fvcParameters.Erv,
                    }
                };
            case AcceptabilityActionTypesNextGen.UpdateReversibilityChangeNextGen:
                return {
                    ...state,
                    analysisSet: {
                        ...state.analysisSet,
                        fev1ReversibilityAbsoluteChange: action.fev1ReversibilityChange.absolute,
                        fev1ReversibilityPercentageChange: action.fev1ReversibilityChange.percentage
                    }
                };
            case AcceptabilityActionTypesNextGen.UpdateGrading:
                return {
                    ...state,
                    analysisSet: {
                        ...state.analysisSet,
                        fev1Grade: action.fev1Grade,
                        fvcGrade: action.fvcGrade
                    }
                };
            case AcceptabilityActionTypesNextGen.UpdatePefBestTestNextGen:
                return {
                    ...state,
                    analysisSet: {
                        ...state.analysisSet,
                        bestPefMin: action.bestPefMin,
                        bestPefSec: action.bestPefSec,
                    } as PefAnalysisSet
                };
            default:
                return state;
        }
    }

    const [state, dispatch] = React.useReducer(reducer, initialState);

    React.useEffect(() => {
        const hydrateAcceptability = () => {
            dispatch(LoadInitialAcceptabilityNextGenAction.create(analysisSet, comparisonAnalysisSet));
        };

        hydrateAcceptability();
    }, [analysisSet, comparisonAnalysisSet])

    return [state, dispatch];
}

export const matchOverreaderAcceptabilityToSite = (state: AcceptabilityNextGenState, action: LoadInitialAcceptabilityNextGenAction, comparisonAnalysisSet?: FvcAnalysisSet | PefAnalysisSet): AcceptabilityNextGenState => {
    if (comparisonAnalysisSet) {
        const notYetCompleted = action.analysisSet.firstCompleted === null || action.analysisSet.firstCompleted === undefined;

        if (notYetCompleted) {
            return {
                ...state,
                analysisSet: {
                    ...action.analysisSet,
                    tests: action.analysisSet.tests.map(t => {
                        if (action.comparisonAnalysisSet.tests.map(cast => cast.testId).includes(t.testId)) {
                            return {
                                ...t,
                                accepted: action.comparisonAnalysisSet.tests.find(cast => cast.testId === t.testId).accepted,
                                fev1Acceptability: action.comparisonAnalysisSet.tests.find(cast => cast.testId === t.testId).fev1Acceptability,
                                fvcAcceptability: action.comparisonAnalysisSet.tests.find(cast => cast.testId === t.testId).fvcAcceptability,
                            }
                        } else {
                            return t;
                        }
                    }),
                },
                changedAcceptability: !state.changedAcceptability,
            };
        }
    } else return null;
}
